import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../components/auth/Alert.js';
import { FormField } from '../components/auth/FormField.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { passwordResetRequest } from '../lib/auth-api.js';

type Stage =
  | { kind: 'form' }
  | { kind: 'sent' }
  | { kind: 'rate_limited' }
  | { kind: 'server_error' }
  | { kind: 'network_error'; message: string };

/**
 * The success copy is intentionally identical for every email we
 * accept — existing, non-existing, syntactically malformed. The
 * server returns 200 in all those cases, and the page mirrors that
 * stance: no enumeration channel between "we sent you a link" and
 * "we didn't because this email is unknown".
 */
const SUCCESS_COPY =
  'Si un compte existe avec cet email, un lien de réinitialisation a été envoyé. ' +
  'Vérifiez votre boîte mail dans les prochaines minutes.';

export function ResetPasswordRequestPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: 'form' });

  const canSubmit = !submitting && email.trim().length > 0;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const result = await passwordResetRequest(email.trim());
    setSubmitting(false);
    if (result.ok) {
      setStage({ kind: 'sent' });
      return;
    }
    if (result.kind === 'rate_limited') {
      setStage({ kind: 'rate_limited' });
      return;
    }
    if (result.kind === 'network_error') {
      setStage({ kind: 'network_error', message: result.message });
      return;
    }
    setStage({ kind: 'server_error' });
  }

  if (stage.kind === 'sent') {
    return (
      <AuthLayout title="Mot de passe oublié">
        <Alert variant="success" title="Demande prise en compte">
          {SUCCESS_COPY}
        </Alert>
        <Link
          to="/login"
          className="btn-ghost w-full mt-6"
          data-testid="reset-request-back-to-login"
        >
          Retour à la connexion
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Mot de passe oublié"
      subtitle="Saisissez votre email, nous vous enverrons un lien de réinitialisation."
      belowCard={
        <p className="text-center text-sm">
          <Link to="/login" className="btn-ghost" data-testid="reset-request-back-link">
            Retour à la connexion
          </Link>
        </p>
      }
    >
      {stage.kind === 'rate_limited' ? (
        <div className="mb-4">
          <Alert variant="warning" title="Trop de demandes">
            Trop de demandes. Patientez quelques minutes avant de réessayer.
          </Alert>
        </div>
      ) : null}
      {stage.kind === 'server_error' ? (
        <div className="mb-4">
          <Alert variant="error">Une erreur est survenue. Réessayez plus tard.</Alert>
        </div>
      ) : null}
      {stage.kind === 'network_error' ? (
        <div className="mb-4">
          <Alert variant="error">{stage.message}</Alert>
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <FormField
          label="Adresse e-mail"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="off"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@exemple.com"
          disabled={submitting}
          required
          data-testid="reset-request-email"
        />

        <button
          type="submit"
          disabled={!canSubmit}
          aria-busy={submitting || undefined}
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="reset-request-submit"
        >
          {submitting ? 'Envoi…' : 'Envoyer le lien'}
        </button>
      </form>
    </AuthLayout>
  );
}
